import { useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";

import { routes } from "~/app/routes/web-routes.ts";
import { customerDetailsSchema } from "~/shared/schemas/customer-schema.ts";
import { apiRoutes } from "~/shared/services/api/api-routes.ts";
import { getAndValidate } from "~/shared/services/api/fetch-client.ts";
import type { Customer } from "~/shared/types/quote-details-types.ts";

export const useAutoDispatchNavigation = () => {
	const { customerId } = useParams();
	const navigate = useNavigate();

	const [selectedCustomer, setSelectedCustomer] = useState<
		Customer | undefined
	>();

	const {
		data: customerDetails,
		isLoading: queryIsCustomerLoading,
		isError,
	} = useQuery({
		queryKey: ["customer", customerId],
		queryFn: async () => {
			if (!customerId) return null;
			const result = await getAndValidate(
				apiRoutes.CUSTOMER.replace(":customerId", customerId),
				customerDetailsSchema,
			);
			if (!result.success) {
				throw new Error(result.error);
			}
			return result.data;
		},
		enabled: !!customerId,
	});

	useEffect(() => {
		if (customerDetails) {
			setSelectedCustomer(customerDetails);
		} else if (!customerId) {
			setSelectedCustomer(undefined);
		}
	}, [customerDetails, customerId]);

	const isCustomerLoading =
		(!!customerId && !selectedCustomer && !isError) || queryIsCustomerLoading;

	const handleSelectCustomer = useCallback(
		(customer: Customer | undefined) => {
			if (customer) {
				setSelectedCustomer(customer);
				navigate(
					routes.autoDispatchWithCustomer.replace(
						":customerId",
						String(customer.id),
					),
				);
			} else {
				setSelectedCustomer(undefined);
				navigate(routes.autoDispatch);
			}
		},
		[navigate],
	);

	return {
		selectedCustomer,
		isCustomerLoading,
		isCustomerError: isError,
		handleSelectCustomer,
	};
};
